'use client';

import { useEffect } from 'react';
import { enhanceErrorWithContext, decodeReactError, createFriendlyErrorMessage } from '@/utils/react-error-decoder';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Enhanced error logging for global errors
    const enhancedError = enhanceErrorWithContext(error, {
      source: 'global-error-boundary',
      digest: error.digest,
      globalError: true
    });

    const decodedMessage = decodeReactError(error.message);

    const errorDetails = {
      ...enhancedError,
      decodedMessage,
      globalError: {
        digest: error.digest,
        errorMessage: error.message,
        errorStack: error.stack,
        decodedMessage,
      }
    };

    // Log to console for development
    console.group('🚨 Global Error Boundary - Detailed Error Report');
    console.error('Original Error:', error);
    console.error('Decoded Message:', decodedMessage);
    console.error('Error Digest:', error.digest);
    console.error('Full Details:', errorDetails);
    console.groupEnd();

    // Send to Coralogix RUM using the existing analytics class
    try {
      if (typeof window !== 'undefined') {
        import('@/app/rum').then(({ rumAnalytics }) => {
          rumAnalytics.error(decodedMessage, {
            ...errorDetails,
            source: 'global-error-boundary',
            originalError: error.message,
            errorStack: error.stack,
            digest: error.digest
          });
        }).catch(err => console.warn('Failed to load RUM analytics:', err));
      }
    } catch (rumError) {
      console.warn('Failed to send error to RUM analytics:', rumError);
    }

    // Dispatch custom event for additional tracking
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('clearitty-global-error', {
        detail: errorDetails
      }));
    }
  }, [error]);

  return (
    <html>
      <body>
        <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 text-red-800 p-4">
          <h2 className="text-2xl font-bold mb-2">🚨 Application Error</h2>
          <p className="text-lg mb-4">Something went wrong at the application level.</p>
          
          <div className="bg-red-100 p-4 rounded-md text-sm text-red-700 max-w-xl overflow-auto mb-4">
            <p className="font-semibold">Error:</p>
            <pre className="whitespace-pre-wrap break-all mb-2">{createFriendlyErrorMessage(error)}</pre>
            
            {process.env.NODE_ENV === 'development' && (
              <>
                <p className="font-semibold">Original Message:</p>
                <pre className="whitespace-pre-wrap break-all mb-2">{error.message}</pre>
                {error.digest && (
                  <>
                    <p className="font-semibold">Error Digest:</p>
                    <pre className="whitespace-pre-wrap break-all">{error.digest}</pre>
                  </>
                )}
              </>
            )}
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={reset}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
